import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
  getDocs,
} from "firebase/firestore";
import { db } from "./firebaseConfig";

const PRESENCE_COLLECTION = "presence";
const PRESENCE_TIMEOUT_MS = 60000; // Consider user offline after 60 seconds of no heartbeat

// Generate a unique session ID for this browser tab
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create a session ID for this tab
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("zodiac_session_id");
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem("zodiac_session_id", sessionId);
  }
  return sessionId;
};

let heartbeatInterval: NodeJS.Timeout | null = null;
let isTracking = false;
let currentUid: string | null = null;

/**
 * Register this user's presence in Firestore
 * Called when user loads the site
 */
export async function registerPresence(uid?: string): Promise<void> {
  if (isTracking) {
    // Already tracking but uid may have changed (user logged in)
    if (uid && uid !== currentUid) {
      currentUid = uid;
      await updateHeartbeat();
    }
    return;
  }

  if (uid) currentUid = uid;

  const sessionId = getSessionId();
  const docRef = doc(db, PRESENCE_COLLECTION, sessionId);

  try {
    const data: Record<string, any> = {
      lastSeen: Timestamp.now(),
      createdAt: Timestamp.now(),
    };
    if (currentUid) data.uid = currentUid;
    await setDoc(docRef, data);

    isTracking = true;

    // Start heartbeat to keep presence alive
    startHeartbeat();

    // Clean up on page unload
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    // Also handle visibility changes (tab hidden/shown)
    document.addEventListener("visibilitychange", handleVisibilityChange);

  } catch (error) {
  }
}

/**
 * Update the heartbeat timestamp
 */
async function updateHeartbeat(): Promise<void> {
  const sessionId = getSessionId();
  const docRef = doc(db, PRESENCE_COLLECTION, sessionId);

  try {
    const data: Record<string, any> = { lastSeen: Timestamp.now() };
    if (currentUid) data.uid = currentUid;
    await setDoc(docRef, data, { merge: true });
  } catch (error) {
    // Silently fail - not critical
  }
}

/**
 * Start the heartbeat interval
 */
function startHeartbeat(): void {
  if (heartbeatInterval) return;

  // Update heartbeat every 30 seconds
  heartbeatInterval = setInterval(() => {
    if (document.visibilityState === "visible") {
      updateHeartbeat();
    }
  }, 30000);
}

/**
 * Stop the heartbeat interval
 */
function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/**
 * Handle page unload - remove presence
 */
function handleUnload(): void {
  const sessionId = getSessionId();
  const docRef = doc(db, PRESENCE_COLLECTION, sessionId);

  // Use sendBeacon for reliable cleanup on page close
  // Fall back to deleteDoc which may not complete
  try {
    deleteDoc(docRef);
  } catch {
    // Best effort cleanup
  }

  stopHeartbeat();
  isTracking = false;
}

/**
 * Handle visibility change - pause/resume heartbeat
 */
function handleVisibilityChange(): void {
  if (document.visibilityState === "visible") {
    // Tab became visible - update heartbeat immediately
    updateHeartbeat();
  }
}

/**
 * Remove this user's presence
 */
export async function removePresence(): Promise<void> {
  const sessionId = getSessionId();
  const docRef = doc(db, PRESENCE_COLLECTION, sessionId);

  try {
    await deleteDoc(docRef);
  } catch (error) {
  }

  stopHeartbeat();
  isTracking = false;

  window.removeEventListener("beforeunload", handleUnload);
  window.removeEventListener("pagehide", handleUnload);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
}

/**
 * Subscribe to real-time player count updates
 * @param callback Function called with updated player count
 * @returns Unsubscribe function
 */
export function subscribeToPlayerCount(
  callback: (count: number) => void
): () => void {
  const presenceRef = collection(db, PRESENCE_COLLECTION);

  // Listen to all presence documents
  const unsubscribe = onSnapshot(presenceRef, (snapshot) => {
    const now = Date.now();
    let activeCount = 0;

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const lastSeen = data.lastSeen?.toMillis() || 0;

      // Count as active if heartbeat is within timeout window
      if (now - lastSeen < PRESENCE_TIMEOUT_MS) {
        activeCount++;
      }
    });

    callback(activeCount);
  }, (error) => {
    // Return a fallback count on error
    callback(1);
  });

  return unsubscribe;
}

/**
 * Get current player count (one-time fetch)
 */
export async function getPlayerCount(): Promise<number> {
  const presenceRef = collection(db, PRESENCE_COLLECTION);
  const cutoffTime = Timestamp.fromMillis(Date.now() - PRESENCE_TIMEOUT_MS);

  try {
    // Query for documents with recent lastSeen
    const q = query(
      presenceRef,
      where("lastSeen", ">", cutoffTime)
    );

    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    return 1; // Fallback
  }
}

/**
 * Subscribe to online status of specific UIDs (for friend list)
 * @param uids Array of user UIDs to monitor
 * @param callback Called with set of online UIDs whenever it changes
 * @returns Unsubscribe function
 */
export function subscribeToFriendPresence(
  uids: string[],
  callback: (onlineUids: Set<string>) => void
): () => void {
  if (uids.length === 0) {
    callback(new Set());
    return () => {};
  }

  const presenceRef = collection(db, PRESENCE_COLLECTION);

  const unsubscribe = onSnapshot(presenceRef, (snapshot) => {
    const now = Date.now();
    const onlineUids = new Set<string>();

    snapshot.docs.forEach((d) => {
      const data = d.data();
      const lastSeen = data.lastSeen?.toMillis() || 0;
      const uid = data.uid;

      if (uid && uids.includes(uid) && now - lastSeen < PRESENCE_TIMEOUT_MS) {
        onlineUids.add(uid);
      }
    });

    callback(onlineUids);
  }, () => {
    callback(new Set());
  });

  return unsubscribe;
}

/**
 * Clean up stale presence documents (run periodically on server or cloud function)
 * For now, we filter them out client-side in subscribeToPlayerCount
 */
export async function cleanupStalePresence(): Promise<number> {
  const presenceRef = collection(db, PRESENCE_COLLECTION);
  const cutoffTime = Timestamp.fromMillis(Date.now() - PRESENCE_TIMEOUT_MS * 2);

  try {
    const q = query(
      presenceRef,
      where("lastSeen", "<", cutoffTime)
    );

    const snapshot = await getDocs(q);
    let deletedCount = 0;

    for (const docSnapshot of snapshot.docs) {
      await deleteDoc(docSnapshot.ref);
      deletedCount++;
    }

    return deletedCount;
  } catch (error) {
    return 0;
  }
}
